import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Check, ArrowForward } from '@mui/icons-material';

const TemplateCardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  position: 'relative',
  '&:hover .hover-overlay': {
    opacity: 1
  }
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  aspectRatio: '4 / 3',
  overflow: 'hidden',
  backgroundColor: '#f5f5f5',
  marginBottom: '16px'
}));

const TemplateImage = styled('img')(({ theme, locked }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  filter: locked ? 'blur(4px)' : 'none',
  transition: 'filter 0.2s ease'
}));

const ComingSoonOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 5
}));

const ComingSoonBadge = styled(Box)(({ theme }) => ({
  backgroundColor: '#1a1a1a',
  color: '#ffffff',
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 600,
  padding: '10px 24px',
  borderRadius: '4px',
  textTransform: 'uppercase',
  letterSpacing: '1px'
}));

const HoverOverlay = styled(Box)(({ theme, selected }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.75)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  opacity: selected ? 1 : 0,
  transition: 'opacity 0.2s ease'
}));

const UseTemplateButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#1a1a1a',
  color: '#ffffff',
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  padding: '10px 24px',
  borderRadius: '4px',
  minWidth: '160px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#333333'
  }
}));

const PreviewButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#ffffff',
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  padding: '10px 24px',
  borderRadius: '4px',
  minWidth: '160px',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: '#ffffff'
  }
}));

const SelectedBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '12px',
  right: '12px',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: '#1a1a1a',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10
}));

const TemplateName = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '16px',
  fontWeight: 500,
  color: '#1a1a1a',
  marginBottom: '4px'
}));

const TemplateCategory = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '11px',
  fontWeight: 500,
  color: '#999999',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
}));

const TemplateCard = ({ template, selected, onClick, onPreview, onUseTemplate, isFirst }) => {
  const [isHovered, setIsHovered] = useState(false);

  const isLocked = template.name !== 'Modern SaaS' && template.name !== 'Premium Dark';

  return (
    <TemplateCardContainer
      onClick={isLocked ? undefined : onClick}
      onMouseEnter={() => !isLocked && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{ cursor: isLocked ? 'default' : 'pointer' }}
      data-tutorial={isFirst && !isLocked ? "template-card" : undefined}
    >
      <ImageContainer>
        <TemplateImage
          src={template.image}
          alt={template.name}
          locked={isLocked}
        />

        {isLocked && (
          <ComingSoonOverlay>
            <ComingSoonBadge>Coming Soon</ComingSoonBadge>
          </ComingSoonOverlay>
        )}

        {selected && !isLocked && (
          <SelectedBadge>
            <Check sx={{ color: '#ffffff', fontSize: 20 }} />
          </SelectedBadge>
        )}

        {!isLocked && (
          <HoverOverlay className="hover-overlay" selected={selected || isHovered}>
            <UseTemplateButton
              endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
              onClick={(e) => {
                e.stopPropagation();
                onUseTemplate(template);
              }}
              data-tutorial={isFirst ? "use-template" : undefined}
            >
              Use Template
            </UseTemplateButton>
            <PreviewButton
              onClick={(e) => {
                e.stopPropagation();
                onPreview(template);
              }}
            >
              Preview
            </PreviewButton>
          </HoverOverlay>
        )}
      </ImageContainer>

      <TemplateName>{template.name}</TemplateName>
      <TemplateCategory>{template.category}</TemplateCategory>
    </TemplateCardContainer>
  );
};

export default TemplateCard;